import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { account, databases, Query, default as client } from '../config/appwrite';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const inactivityTimeoutRef = useRef(null);
  const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

  const checkUserSession = useCallback(async () => {
    try {
      const me = await account.get();
      if (me && me.$id) {
        const fromDb = await getUserDataFromDatabase(me.$id, me.email);
        setUser(fromDb || mapUserFromAccount(me));
      }
    } catch (error) {
      console.log('No active session found');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
    // Session restore runs once on mount; helpers are stable for this provider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const testAppwriteConnection = async () => {
    try {
      console.log('Testing Appwrite connection...');
      // Try to get account info to test connection
      const accountInfo = await account.get();
      console.log('Appwrite connection test successful:', accountInfo);
    } catch (error) {
      console.log('Appwrite connection test failed (this is normal if not logged in):', error.message);
    }
  };

  useEffect(() => {
    checkUserSession();
    testAppwriteConnection();
  }, [checkUserSession]);

  const usersCollectionId =
    process.env.REACT_APP_APPWRITE_USERS_COLLECTION_ID || 'users';

  const mapUserDoc = (userDoc) => ({
    $id: userDoc.$id,
    firstname: userDoc.firstname,
    surname: userDoc.surname,
    email: userDoc.email,
    number: userDoc.number,
    id: userDoc.id,
    role: userDoc.role,
    roles: userDoc.roles,
    title: userDoc.title,
    Acc: userDoc.Acc,
    bank: userDoc.bank,
    $createdAt: userDoc.$createdAt,
    $updatedAt: userDoc.$updatedAt,
    isAuthenticated: true
  });

  /** When Auth exists but no `users` row matches — still allow app access. */
  const mapUserFromAccount = (accountUser) => {
    const name = (accountUser.name || '').trim();
    const parts = name.split(/\s+/).filter(Boolean);
    return {
      $id: accountUser.$id,
      firstname: parts[0] || '',
      surname: parts.length > 1 ? parts.slice(1).join(' ') : '',
      email: accountUser.email || '',
      number: accountUser.phone || '',
      id: null,
      role: undefined,
      title: undefined,
      Acc: undefined,
      bank: undefined,
      $createdAt: accountUser.$createdAt,
      $updatedAt: accountUser.$updatedAt,
      isAuthenticated: true,
      profileFromAuthOnly: true
    };
  };

  /**
   * Scan users collection (paginated) and match email case-insensitively.
   * Used when email index is missing or stored casing differs from Auth email.
   */
  const findUserDocByEmailScan = async (databaseId, collectionId, emailFallback) => {
    const want = String(emailFallback || '').trim().toLowerCase();
    if (!want) return null;
    let offset = 0;
    const limit = 100;
    while (offset < 20000) {
      try {
        const res = await databases.listDocuments(databaseId, collectionId, [
          Query.limit(limit),
          Query.offset(offset)
        ]);
        const docs = res.documents || [];
        const found = docs.find(
          (d) =>
            (d.email || '').toString().trim().toLowerCase() === want ||
            (d.Email || '').toString().trim().toLowerCase() === want
        );
        if (found) return found;
        if (docs.length < limit) break;
        offset += limit;
      } catch (pageErr) {
        console.warn('findUserDocByEmailScan page failed:', pageErr.message || pageErr);
        break;
      }
    }
    return null;
  };

  /**
   * Load profile from `users` collection.
   * 1) getDocument by Auth user id (document id must match Auth user id)
   * 2) Query by $id
   * 3) Query by email (needs index on email in Appwrite)
   * 4) Paginated scan by email (works without email index)
   */
  const getUserDataFromDatabase = async (userId, emailFallback = null) => {
    const databaseId = process.env.REACT_APP_APPWRITE_DATABASE_ID || 'jadartx';
    const collectionId = usersCollectionId;
    if (!userId) {
      console.warn('getUserDataFromDatabase: missing userId');
      return null;
    }
    console.log('Querying database:', { databaseId, collectionId, userId, emailFallback });

    try {
      try {
        const doc = await databases.getDocument(databaseId, collectionId, userId);
        if (doc) return mapUserDoc(doc);
      } catch (e) {
        if (e.code !== 404 && !String(e.message || '').includes('404')) {
          console.warn('getDocument(collection, userId):', e.message || e);
        }
      }

      try {
        const response = await databases.listDocuments(databaseId, collectionId, [
          Query.equal('$id', userId)
        ]);
        if (response.documents.length > 0) {
          return mapUserDoc(response.documents[0]);
        }
      } catch (e) {
        console.warn('Query.equal($id) failed:', e.message || e);
      }

      if (emailFallback) {
        const trimmed = String(emailFallback).trim();
        const variants = [...new Set([trimmed, trimmed.toLowerCase(), trimmed.toUpperCase()])];
        for (const v of variants) {
          try {
            const response = await databases.listDocuments(databaseId, collectionId, [
              Query.equal('email', v)
            ]);
            if (response.documents.length > 0) {
              return mapUserDoc(response.documents[0]);
            }
          } catch (qErr) {
            console.warn('Query.equal(email) failed (add index on email or rely on scan):', qErr.message || qErr);
          }
        }

        const scanned = await findUserDocByEmailScan(databaseId, collectionId, emailFallback);
        if (scanned) return mapUserDoc(scanned);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
    return null;
  };

  const login = async (email, password) => {
    try {
      console.log('Attempting login with:', { 
        email, 
        endpoint: client.config.endpoint, 
        projectId: client.config.project
      });
      
      // Check if user already has an active session
      try {
        const existingSession = await account.get();
        if (existingSession && existingSession.$id) {
          console.log('Active session found, using existing session');
          const fromDb = await getUserDataFromDatabase(existingSession.$id, existingSession.email);
          const userData = fromDb || mapUserFromAccount(existingSession);
          if (userData) {
            setUser(userData);
            return { success: true, user: userData };
          } else {
            // If session exists but no user data, try to create new session
            // First delete the existing session
            try {
              await account.deleteSession('current');
            } catch (deleteError) {
              console.log('Could not delete existing session:', deleteError);
            }
          }
        }
      } catch (sessionError) {
        // No active session, proceed with login
        console.log('No active session found, proceeding with login');
      }
      
      await account.createEmailPasswordSession(email, password);

      const me = await account.get();
      if (!me || !me.$id) {
        throw new Error('Could not load account after login');
      }

      const fromDb = await getUserDataFromDatabase(me.$id, me.email || email);
      const userData = fromDb || mapUserFromAccount(me);

      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Login failed. Please try again.';
      
      // Handle active session error - if user already logged in, get their data
      if (error.message && (error.message.toLowerCase().includes('session is active') ||
          error.message.toLowerCase().includes('prohibited when a session is active'))) {
        try {
          const existingSession = await account.get();
          if (existingSession && existingSession.$id) {
            const fromDb = await getUserDataFromDatabase(existingSession.$id, existingSession.email);
            const userData = fromDb || mapUserFromAccount(existingSession);
            if (userData) {
              setUser(userData);
              return { success: true, user: userData };
            }
          }
        } catch (sessionError) {
          console.error('Error getting existing session:', sessionError);
        }
        // If we can't get the session, return error
        return {
          success: false,
          error: 'You are already logged in. Please refresh the page.'
        };
      }
      
      // Appwrite Cloud resource limit
      if (error.code === 402 || (error.message && error.message.toLowerCase().includes('resource limit'))) {
        return {
          success: false,
          error: 'Appwrite Cloud resource limit exceeded. Upgrade plan, create a new project, or switch the endpoint to another project for development.'
        };
      }

      const msg = String(error?.message || '');

      // Check for CORS errors
      if (msg.includes('CORS') || msg.includes('Access-Control-Allow-Origin') || msg.includes('blocked by CORS policy')) {
        errorMessage = 'This website is not allowed by Appwrite. Add it under Console → Platforms (use jadartx.co.tz, not www).';
      } else if (msg.includes('Failed to fetch') || error?.name === 'TypeError') {
        errorMessage = 'Cannot reach Appwrite API. Use https://jadartx.co.tz (not www) or restart local npm run dev. API: https://api.jadartx.co.tz';
      } else if (msg.includes('Invalid credentials')) {
        errorMessage = 'Invalid email or password. Please check your credentials.';
      } else if (msg.includes('User not found')) {
        errorMessage = 'No account found with this email address.';
      } else if (msg) {
        errorMessage = msg;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const logout = useCallback(async () => {
    try {
      // Clear inactivity timer
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      
      await account.deleteSession('current');
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails on server, clear local state
      setUser(null);
    }
  }, []);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    // Clear existing timer
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    // Only set timer if user is authenticated
    if (user && user.isAuthenticated) {
      // Set new timer
      inactivityTimeoutRef.current = setTimeout(() => {
        console.log('User inactive for 5 minutes. Logging out...');
        logout();
      }, INACTIVITY_TIMEOUT);
    }
  }, [user, logout]);

  // Setup activity listeners
  useEffect(() => {
    if (!user || !user.isAuthenticated) {
      // Clear timer if user is not authenticated
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      return;
    }

    // Reset timer on initial load
    resetInactivityTimer();

    // Activity events that reset the timer
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity, true);
    });

    // Cleanup function
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity, true);
      });
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
    };
  }, [user, resetInactivityTimer]);

  const isAuthenticated = () => {
    return user && user.isAuthenticated;
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
